import { AxiosResponse } from "axios";
import { useConfigStore } from "../../store/configuration.store";
import { http } from "../api-http";
import { BadRequestError } from "../exceptions/bad-request.error";
import { RequiredError } from "../exceptions/required.error";
import { TokenRequestResult } from "../models/auth.model";
import { UserFavorite } from "../models/favorite.model";
import { Station } from "../models/station.model";
import { User, UserMetadata } from "../models/user.model";

export class AuthService {

    private static  _instance: AuthService | null = null;

    static getInstance(): AuthService {
        if(!AuthService._instance) {
            AuthService._instance = new AuthService();
        }
        return AuthService._instance;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {
    }

    login(login: string, password: string): Promise<TokenRequestResult> {
        const configStore = useConfigStore();

        if(!configStore.loaded) {
            configStore.load();
        }

        return http.post<TokenRequestResult>('/auth/access_token', {
            grant_type: 'password',
            client_id: configStore.clientId,
            client_secret: configStore.clientSecret,
            scope: '',
            username: login,
            password,
        },  
        { 
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }   
        }
        )
        .then((response: AxiosResponse<TokenRequestResult>) => response.data)
    }

    logout(): Promise<void> {
        return http.get('/auth/logout');
    }


    refreshToken(refreshToken: string): Promise<TokenRequestResult> {
        const configStore = useConfigStore();
        return http.post<TokenRequestResult>('/auth/access_token', {
            grant_type: 'refresh_token',
            client_id: configStore.clientId,
            client_secret: configStore.clientSecret,
            refresh_token: refreshToken,
        }).then((response: AxiosResponse<TokenRequestResult>) => response.data)
    }

    async getUserProfile(): Promise<User> {
        return http.get<User>('/api/userinfo').then((response: AxiosResponse<User>) => response.data);
    }

    async updateUserMetadata(metadata: Partial<UserMetadata>): Promise<void> {
        return http.post<void>('/api/userinfo/update-metadata', metadata).then((response: AxiosResponse<void>) => response.data);
    }

    async forgotPassword(email: string): Promise<void> {
        return http.post<void>('/auth/forgotten-password', { email }).then((response: AxiosResponse<void>) => response.data);
    }

    async register(email: string, password): Promise<void> {
        return http.post<void>('/auth/register', { email, password }).then((response: AxiosResponse<void>) => response.data);
    }

    async addUserFavorite(station: Station): Promise<void> {
        if(!station || !station.id || !station.location|| !station.name) {
            throw new RequiredError(this.constructor.name, 'Missing parameter(s). Required are station.id, station.location, station.name');
        }

        if(!station.location.lat && !station.location.lon) {
            throw new BadRequestError(this.constructor.name, 'Could not add station to favorite. Station is not geolocalized');
        }

        const favorite: UserFavorite = {
            coordinates: station.location,
            country: station.metadata["elichens/public/locality"],
            city: station.metadata["elichens/public/locality"],
            id: station.id,
            name: station.name,
            station_id: station.id,
            street: station.metadata["elichens/public/locality"],
        }

        return http.post<void>('/api/userinfo/add-favorite', favorite).then((response: AxiosResponse<void>) => response.data);
    }

    async removeUserFavorite(station: Station): Promise<void> {
        if(!station || !station.id) {
            throw new RequiredError(this.constructor.name, 'Missing parameter station.id');
        }

        return http.post<void>('/api/userinfo/remove-favorite', { id: station.id}).then((response: AxiosResponse<void>) => response.data);
    }
}